import t from "@/lang/lang";
import { TimeNumberInput } from "@/shared/components/inputs/time-number-input";
import { useQpmCatalogItemSetServiceTimesMutation } from "@/services/backend/qpm/catalog/api/catalog-api";
import { Button } from "@/shared/components/ui/button";
import { Label } from "@/shared/components/ui/label";
import { Info } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/shared/components/ui/tooltip";
import { usePersistenceContext } from "@/shared/lib/persistence-state/context";
import { PersistenceState } from "@/shared/lib/persistence-state/persistence-state";

const tooltipContentClassName =
  "bg-background text-foreground max-w-sm border text-left text-xs [&>svg]:bg-background [&>svg]:fill-background";

export function CatalogItemTimesComponent({
  catalogItemId,
  install,
  maintain,
}: {
  catalogItemId: string;
  install: number;
  maintain: number;
}) {
  const [update] = useQpmCatalogItemSetServiceTimesMutation();
  const { setPersistenceState } = usePersistenceContext();
  const timeHint = t(
    "Diese Zeiten werden in der Bestandsaufnahme verwendet und nicht für die Kalkulationszeiten.",
  );

  const persist = (next: { install: number; maintain: number }) => {
    setPersistenceState(PersistenceState.SAVING);
    update({
      catalogItemId,
      install: next.install,
      maintain: next.maintain,
    })
      .unwrap()
      .then(() => setPersistenceState(PersistenceState.SAVED))
      .catch(() => setPersistenceState(PersistenceState.ERROR));
  };

  return (
    <div className="space-y-2">
      <div className="grid grid-cols-4 gap-1.5">
        <div className="col-span-2 flex items-center gap-2">
          <Label>{t("Installationszeit")}</Label>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="text-muted-foreground hover:text-foreground h-5 w-5"
                  aria-label={t("Hinweis zu Servicezeiten")}
                >
                  <Info className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent className={tooltipContentClassName}>
                <p>{timeHint}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        <div className="col-span-2">
          <TimeNumberInput
            value={install}
            onChange={(value) => persist({ install: value, maintain })}
          />
        </div>
      </div>

      <div className="grid grid-cols-4 gap-1.5">
        <div className="col-span-2 flex items-center gap-2">
          <Label>{t("Servicezeit")}</Label>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="text-muted-foreground hover:text-foreground h-5 w-5"
                  aria-label={t("Hinweis zu Servicezeiten")}
                >
                  <Info className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent className={tooltipContentClassName}>
                <p>{timeHint}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        <div className="col-span-2">
          <TimeNumberInput
            value={maintain}
            onChange={(value) => persist({ install, maintain: value })}
          />
        </div>
      </div>
    </div>
  );
}
